import { _decorator, tween, Tween, CurveRange, ITweenOption, Component } from 'cc';
import { DEV } from 'cc/env';
import Delay from '../../../Utils/Delay';

const { ccclass, property } = _decorator;

enum EventType {
    OnTweenEffectStart = 'OnTweenEffectStart',
    OnTweenEffectStop = 'OnTweenEffectStop',
}

@ccclass('ITweenEffect')
export default abstract class ITweenEffect<T> extends Component {
    public static readonly EVENT_TYPE = EventType;

    @property({ tooltip: DEV && '開始後播放' })
    private readonly playOnLoad: boolean = true;

    @property({ tooltip: DEV && '啟用後播放' })
    private readonly playOnEnable: boolean = false;

    @property({ tooltip: DEV && '循環播放' })
    private readonly loop: boolean = true;

    @property({ tooltip: DEV && '來返播放' })
    private readonly pingpong: boolean = true;

    @property({ tooltip: DEV && '動畫時長(單位為毫秒)' })
    private readonly duration: number = 1000.0;

    @property({ tooltip: DEV && '隨機起始偏移進度' })
    private readonly randomStartProgressOffset: boolean = false;

    @property({
        tooltip: DEV && '起始偏移進度 (0~1之間)',
        visible() {
            return !this.randomStartProgressOffset;
        },
    })
    private readonly startProgressOffset: number = 0;

    @property({ type: CurveRange, tooltip: DEV && '速度曲線' })
    private readonly curve: CurveRange = new CurveRange();

    protected declare from: T;
    protected declare end: T;

    private _currentTween: Tween<any> = null;
    private _isRunning = false;
    private _startTime: number = 0;

    public get elapsedTime(): number {
        if (!this._isRunning) {
            return 0;
        }
        return (Date.now() - this._startTime) % (this.pingpong ? this.duration * 2 : this.duration);
    }

    public set elapsedTime(time: number) {
        if (!this._isRunning) {
            return;
        }
        this._startTime = Date.now() - time;
        this._currentTween.setTime(time / 1000);
    }

    public get isRunning(): boolean {
        return this._isRunning;
    }

    public get tween() {
        return this._currentTween;
    }

    protected abstract get target();

    public onLoad() {}

    public onEnable() {
        if (this.playOnEnable && !this._isRunning) {
            this.play();
        }
    }

    public start() {
        if (this.playOnLoad && !this._isRunning) {
            this.play();
        }
    }

    public onDestroy() {
        this.stop();
    }

    public play(from?: T, to?: T) {
        from = from ?? this.from;
        to = to ?? this.end;
        this._isRunning = true;
        this._startTime = Date.now();
        this.emit(EventType.OnTweenEffectStart);
        return this.run(from, to);
    }

    /**
     * Stop the process if it is running.
     */
    public stop() {
        if (!this.isRunning) {
            return;
        }
        this.emit(EventType.OnTweenEffectStop);
        this._isRunning = false;
        this.finish();
    }

    protected async run(from: T, to: T) {
        await this.animation(from, to, this.randomStartProgressOffset ? Math.random() : this.startProgressOffset);
        if (!this.loop) {
            this.stop();
            return;
        }
        if (this.isRunning) {
            this.animation(from, to, 0, this.loop);
            await Delay.forever();
        }
    }

    protected async animation(from: T, to: T, skipProgress: number = 0, loop: boolean = false): Promise<void> {
        const fromParam = this.buildParam(from);
        const toParam = this.buildParam(to);
        this._currentTween = tween(this.target)
            .set(fromParam)
            .to(this.duration / 1000.0, toParam, this.options(from, to, false));
        if (this.pingpong) {
            this._currentTween = this._currentTween.set(fromParam).to(this.duration / 1000.0, toParam, this.options(from, to, true));
        }
        if (loop) {
            // repeat forever. repeatForever() has trouble with set(), use union().repeat(Number.MAX_SAFE_INTEGER) instead.
            this._currentTween = this._currentTween.union().repeat(Number.MAX_SAFE_INTEGER);
        }
        await this._currentTween.promisifyStart((skipProgress * this.duration) / 1000);
    }

    protected async finish() {
        if (!this._currentTween) {
            return;
        }
        this._currentTween.stop();
        this._currentTween = null;
    }

    protected options(from: T, to: T, reverse: boolean): ITweenOption<any> {
        return {
            easing: (t) => this.curve.evaluate(reverse ? 1 - t : t),
        };
    }

    protected abstract buildParam(value: T);
}
